import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { mark, measureAndLog } from '@hotjar/performance';

import { useSiteStore, useSites } from '@hotjar/site';

import { getLoggedIn } from '@hotjar/webapp/domains/common/user-data';
import { getRoute, matchPathAndRoute } from '@hotjar/webapp/utils/routing';
import { PERMISSION_REQUIRED, SITE } from '@hotjar/webapp/constants/routes';
import { MARKS } from '@hotjar/webapp/performance';

interface AuthenticatedRouteProps {
  children: JSX.Element;
}

const SITES_PATH_BASE = 'sites/';
const SITE_ID_WILDCARD = '*';

/**
 * A component that checks a user has access to a given site otherwise redirects the user to an error page.
 *
 * <Route path="/<some_path>" element={
 *   <SiteAccessRequiredRoute>
 *     <MyComponent />
 *   </SiteAccessRequiredRoute>
 * } />
 */
export const SiteAccessRequiredRoute = ({
  children,
}: AuthenticatedRouteProps): JSX.Element | null => {
  const [isAccessAllowed, setAccessAllowed] = useState(false);
  const isLoggedIn = getLoggedIn();
  const { siteId: siteIdParam } = useParams();
  const preferredCurrentSiteId = useSiteStore((state) => state.preferredCurrentSiteId);
  const {
    query: { data: sites, isLoading, isPlaceholderData },
  } = useSites();
  const navigate = useNavigate();
  const location = useLocation();
  const siteId = siteIdParam === SITE_ID_WILDCARD ? preferredCurrentSiteId : siteIdParam;
  const isPermissionsRequiredPage = matchPathAndRoute(
    location.pathname,
    PERMISSION_REQUIRED.BASE.path,
  );
  const nextUrl =
    location.pathname !== undefined && location.pathname.length > 0
      ? `?next=${encodeURIComponent(location.pathname + location.search)}`
      : '';

  // #region Check for siteId wildcard
  useEffect(() => {
    // If there is a wildcard in the URL and preferredCurrentSiteId is missing from localStorage,
    // redirect to the site list. This is a rare edge case.
    if (
      siteIdParam === SITE_ID_WILDCARD &&
      (preferredCurrentSiteId === undefined || !Number(preferredCurrentSiteId))
    ) {
      navigate(SITE.LIST.path, {
        replace: true,
      });
    }
  }, [navigate, preferredCurrentSiteId, siteIdParam]);

  useEffect(() => {
    // If there is a wildcard in the URL and we have preferredCurrentSiteId in localStorage,
    // redirect to the the preferred site. This is used by Marketing URLs.
    if (siteIdParam === SITE_ID_WILDCARD && Number(preferredCurrentSiteId)) {
      const newPath = location.pathname.replace(
        `${SITES_PATH_BASE}${SITE_ID_WILDCARD}`,
        `${SITES_PATH_BASE}${preferredCurrentSiteId}`,
      );
      navigate(newPath, {
        replace: true,
      });
    }
  }, [location.pathname, navigate, preferredCurrentSiteId, siteIdParam]);
  // #endregion

  // #region Check for site access
  useEffect(() => {
    // If we're already on the permissions-required page, just render it.
    // This prevents an infinite loop.
    if (isPermissionsRequiredPage) {
      setAccessAllowed(true);
      return;
    }

    // When user is not logged in, there are no sites so redirect immediately.
    // This is only applicable to routes not wrapped with <AuthenticatedRoute>, mostly Angular pages.
    // The permission-required page has a special version for unauthenticated users.
    if (!isLoggedIn && siteId !== undefined && !Number.isNaN(siteId)) {
      setAccessAllowed(false);
      navigate(
        `${getRoute(PERMISSION_REQUIRED.BASE.path, {
          siteId,
          replace: true,
        })}${nextUrl}`,
      );
      return;
    }

    if (isLoading || isPlaceholderData || !sites || siteId === undefined || Number.isNaN(siteId)) {
      return;
    }

    const hasSiteAccess = !!sites?.find((site) => site.id === Number(siteId));
    if (hasSiteAccess) {
      setAccessAllowed(true);
    } else {
      setAccessAllowed(false);
      navigate(
        `${getRoute(PERMISSION_REQUIRED.BASE.path, {
          siteId,
          replace: true,
        })}${nextUrl}`,
      );
    }
  }, [
    nextUrl,
    isLoading,
    isLoggedIn,
    isPermissionsRequiredPage,
    isPlaceholderData,
    navigate,
    siteId,
    sites,
  ]);
  // #endregion

  useEffect(() => {
    // Perf: Mark site access route wrapper render timings
    if (isLoading) {
      mark(MARKS.APP_ROUTE_SITE_INITIALIZED);
    }

    measureAndLog({
      name: MARKS.APP_ROUTE_SITE_RENDERED,
      startMark: MARKS.APP_ROUTE_SITE_INITIALIZED,
      addMark: true,
    });
  }, [isLoading]);

  if (process.env.NODE_ENV === 'development' && siteIdParam === undefined) {
    throw new Error(`
      This route does not have a siteId parameter.
      Are you sure your component needs to be wrapped in a SiteAccessRequiredRoute?
    `);
  }

  if (isAccessAllowed) {
    return children;
  }

  return null;
};
